import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CaretRight, SignOut, List, X } from "@phosphor-icons/react";
import { Sidebar } from "./Sidebar";
import { SettingsModal } from "../SettingsModal";
import { WelcomePopup } from "../shared/WelcomePopup";
import { FloatingMessageButton } from "../shared/FloatingMessageButton";
import { ScriptPanelLauncher } from "../shared/ScriptPanelLauncher";
import { ScriptPanel } from "../rep/ScriptPanel";
import { SecurityBadge } from "../shared/SecurityBadge";
import { PageWatermark } from "../shared/PageWatermark";
import { NotificationBell } from "./NotificationBell";
import { MessagingPanel } from "./MessagingPanel";
import { RepSearchDropdown } from "../admin/RepSearchDropdown";
import { useAuth } from "../../context/AuthContext";
import { useSiteBanner } from "../../hooks/useSiteBanner";
import { SITE_BANNER_HEIGHT } from "./SiteBanner";
import { useNateM, NATE_M_EXPANDED_WIDTH, NATE_M_MINIMIZED_WIDTH } from "../../context/NateMContext";
import { api } from "../../lib/api";

const IDLE_TIMEOUT_ROLES = ["affiliate", "manager"];
const IDLE_WARN_MS = 23 * 60 * 1000;

export function DashboardLayout({ navItems, topRight, children, moduleName, moduleBackPath }) {
  const { user, logout } = useAuth();
  const banner = useSiteBanner();
  const { minimized, w9Gated } = useNateM();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const isClientType = user?.role === "client" || user?.role === "affiliate";
  const canUseScripts = (user?.role === "affiliate" || user?.role === "manager") && !w9Gated;
  const isAdmin = user?.role === "admin";
  const [welcomeOpen, setWelcomeOpen] = useState(isClientType && user?.welcome_shown === false);
  const idleWarnedRef = useRef(false);

  useEffect(() => {
    if (user?.role !== "affiliate" || user?._impersonating) return;
    const ping = () => api.post("/rep/session/heartbeat").catch(() => {});
    ping();
    const interval = setInterval(ping, 60000);
    return () => clearInterval(interval);
  }, [user?.role, user?._impersonating]);

  // Sep 19 2026 fix — real mouse/keyboard/click activity now also keeps the SERVER-side idle
  // clock fresh (not just the local warning-toast timer below). Before this, a rep/manager could
  // be genuinely active on-screen (reading, typing a note) for 25+ minutes without firing a
  // single business API call, silently hit the server's real idle cutoff, and then any incidental
  // background poll would 401. Throttled to one ping per 45s so it's cheap even during a flurry
  // of clicks/typing.
  useEffect(() => {
    if (!IDLE_TIMEOUT_ROLES.includes(user?.role) || user?._impersonating) return;
    let lastPing = 0;
    const onActivityPing = () => {
      const now = Date.now();
      if (now - lastPing < 45000) return;
      lastPing = now;
      api.post("/auth/heartbeat").catch(() => {});
    };
    onActivityPing();
    window.addEventListener("mousemove", onActivityPing);
    window.addEventListener("keydown", onActivityPing);
    window.addEventListener("click", onActivityPing);
    window.addEventListener("scroll", onActivityPing);
    window.addEventListener("touchstart", onActivityPing);
    return () => {
      window.removeEventListener("mousemove", onActivityPing);
      window.removeEventListener("keydown", onActivityPing);
      window.removeEventListener("click", onActivityPing);
      window.removeEventListener("scroll", onActivityPing);
      window.removeEventListener("touchstart", onActivityPing);
    };
  }, [user?.role, user?._impersonating]);

  // Section 7, Session Rule 2 — a "You'll be logged out in 2 minutes" toast at the 23-minute
  // idle mark (server enforces the real 25-minute cutoff independently). Admin is exempt.
  useEffect(() => {
    if (!IDLE_TIMEOUT_ROLES.includes(user?.role)) return;
    let warnTimer;
    const resetWarnTimer = () => {
      idleWarnedRef.current = false;
      clearTimeout(warnTimer);
      warnTimer = setTimeout(() => {
        if (idleWarnedRef.current) return;
        idleWarnedRef.current = true;
        toast.warning("You will be logged out in 2 minutes due to inactivity.", {
          duration: 20000,
          action: { label: "Stay Logged In", onClick: () => { api.get("/auth/me").catch(() => {}); resetWarnTimer(); } },
        });
      }, IDLE_WARN_MS);
    };
    const onActivity = () => resetWarnTimer();
    resetWarnTimer();
    window.addEventListener("mousemove", onActivity);
    window.addEventListener("keydown", onActivity);
    window.addEventListener("click", onActivity);
    window.addEventListener("touchstart", onActivity);
    return () => {
      clearTimeout(warnTimer);
      window.removeEventListener("mousemove", onActivity);
      window.removeEventListener("keydown", onActivity);
      window.removeEventListener("click", onActivity);
      window.removeEventListener("touchstart", onActivity);
    };
  }, [user?.role]);

  // Admin Tool 5 — content protection on the rep/manager dashboard: no right-click, no
  // Ctrl+S/Ctrl+P/browser print, and CSS user-select:none on the whole app shell. Admin exempt.
  useEffect(() => {
    if (user?.role !== "affiliate" && user?.role !== "manager") return;
    const blockContextMenu = (e) => e.preventDefault();
    const blockKeys = (e) => {
      const key = (e.key || "").toLowerCase();
      if ((e.ctrlKey || e.metaKey) && (key === "s" || key === "p")) e.preventDefault();
    };
    document.addEventListener("contextmenu", blockContextMenu);
    document.addEventListener("keydown", blockKeys);
    document.body.classList.add("igc-content-protected");
    return () => {
      document.removeEventListener("contextmenu", blockContextMenu);
      document.removeEventListener("keydown", blockKeys);
      document.body.classList.remove("igc-content-protected");
    };
  }, [user?.role]);

  // New Build 4 (Sep 18 2026) — a Sign Out control now lives in the top navbar itself, on every
  // page, for every role — not just the sidebar footer.
  const roleTopRight = topRight || (isAdmin ? <div className="flex items-center gap-3"><RepSearchDropdown /><NotificationBell /></div> : null);
  // Neo Iteration 134, Item 6 — the direct-messaging bell must be visible on every page for
  // every role, even pages that pass their own custom `topRight` (rep/manager dashboards), so
  // it's injected unconditionally here rather than folded into roleTopRight above.
  const topRightContent = (
    <>
      {roleTopRight}
      <MessagingPanel />
      <button
        data-testid="top-nav-logout-button"
        onClick={logout}
        title="Sign out of your account"
        className="flex items-center gap-1.5 text-igc-muted hover:text-igc-danger transition-colors text-sm"
      >
        <SignOut size={16} weight="bold" /> Sign Out
      </button>
    </>
  );
  const nateMMargin = minimized ? NATE_M_MINIMIZED_WIDTH : NATE_M_EXPANDED_WIDTH;

  return (
    <div className="min-h-screen bg-igc-bg relative" style={{ paddingTop: SITE_BANNER_HEIGHT(user, banner) }} data-testid="dashboard-layout-root">
      <PageWatermark />
      <Sidebar navItems={navItems} onOpenSettings={() => setSettingsOpen(true)} moduleName={moduleName} moduleBackPath={moduleBackPath} mobileOpen={mobileSidebarOpen} onMobileClose={() => setMobileSidebarOpen(false)} />
      <div className="ml-0 md:ml-64 flex flex-col min-h-screen relative z-[1] transition-[margin-right] duration-200" style={{ marginRight: nateMMargin }}>
        <div className="flex justify-between items-center gap-3 px-4 md:px-10 py-5 border-b border-igc-gold/10">
          <div className="flex items-center gap-3">
            <button
              data-testid="mobile-sidebar-toggle-button"
              onClick={() => setMobileSidebarOpen((v) => !v)}
              title="Opens or closes the navigation menu. On a phone screen there isn't room to show the full menu at all times, so it's tucked away behind this button — tap it any time you need to jump to a different page."
              className="md:hidden flex items-center justify-center w-9 h-9 text-igc-cream border border-igc-gold/25 rounded-md shrink-0"
            >
              {mobileSidebarOpen ? <X size={18} weight="bold" /> : <List size={18} weight="bold" />}
            </button>
            {isClientType && <FloatingMessageButton />}
          </div>
          <div className="flex items-center gap-3">{topRightContent}</div>
        </div>
        {moduleBackPath && (
          <div className="flex items-center gap-2 px-4 md:px-10 pt-6 text-xs" data-testid="page-breadcrumb">
            <Link to={moduleBackPath} data-testid="breadcrumb-back-link" title="Go back to your main dashboard home screen" className="flex items-center gap-1.5 text-igc-muted hover:text-igc-goldText transition-colors">
              <ArrowLeft size={14} weight="bold" /> Home
            </Link>
            <CaretRight size={10} className="text-igc-muted/50" />
            <span className="text-igc-cream" data-testid="breadcrumb-current-page">{moduleName}</span>
          </div>
        )}
        <main className="p-4 md:p-10 flex-1 max-w-full overflow-x-hidden">{children}</main>
        <SecurityBadge variant="footer" />
      </div>
      <SettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />
      {isClientType && <WelcomePopup open={welcomeOpen} onOpenChange={setWelcomeOpen} />}
      {canUseScripts && <ScriptPanelLauncher />}
      {canUseScripts && <ScriptPanel />}
    </div>
  );
}
